import useCuentasPropietarios from './useCuentasPropietarios';
import SearchableSelect from 'components/SearchableSelect';
import PropertyCombobox from 'components/PropertyCombobox';
import Paginacion from 'components/Paginacion';
import styles from './CuentasPropietarios.module.css';

// Cuentas de propietarios (Ola 1.2): antes de una asamblea, el admin ve qué
// propietarios ya activaron su cuenta (correo validado) y tienen teléfono, a
// quiénes hay que perseguir, y puede reenviarles el correo de activación
// (a todos, a los seleccionados, o solo a los que siguen sin activar).
// Con una asamblea seleccionada agrega asistencia y consentimiento.

export default function CuentasPropietarios() {
  const {
    setPropSel, propId, setPropId, asmId, setAsmId, data, loading, downloading, alert, setAlert, q,
    setQ, soloProblemas, setSoloProblemas, soloRebotados, setSoloRebotados, pageSize, setPageSize,
    selected, setSelected, menuOpen, setMenuOpen, sending, menuRef, propAssemblies, conAsamblea,
    download, rows, safePage, pageRows, irAPagina, conCorreo, sinActivar, seleccionables,
    allVisibleSelected, toggleOne, toggleAllVisible, resend, Pill,
  } = useCuentasPropietarios();

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-address-book ${styles.iconoCabecera}`} />Cuentas de propietarios</h1>
          <p>Revisa quién ya activó su cuenta y sus datos, y reenvía el correo de activación a quien haga falta</p>
        </div>
        <div className={styles.acciones}>
          <div className={styles.grupoPropiedad}>
            <i className={`fas fa-building ${styles.iconoPropiedad}`} />
            <PropertyCombobox value={propId} onChange={(id, p) => { setPropId(id); setAsmId(''); setPropSel(p); }} placeholder="Selecciona una propiedad" />
          </div>
          {propId && (
            <SearchableSelect value={asmId} onChange={(id) => setAsmId(id)} placeholder="Sin asamblea (solo datos)" minWidth={200} maxWidth={260} ariaLabel="Asamblea"
              options={propAssemblies.map(a => ({ value: a.id, label: `Asamblea #${a.number_assembly}${a.status === 'en_progreso' ? ' 🟢' : ''}` }))} />
          )}

          {/* Reenviar correo de activación */}
          <div ref={menuRef} className={styles.menuCaja}>
            <button className="btn btn-secondary" disabled={!propId || sending || !conCorreo}
              onClick={() => setMenuOpen(o => !o)}>
              {sending
                ? <><i className="fas fa-spinner fa-spin" /> Enviando...</>
                : <><i className="fas fa-paper-plane" /> Reenviar correo de activación <i className={`fas fa-caret-down ${styles.iconoCaret}`} /></>}
            </button>
            {menuOpen && (
              <div className={styles.menuPanel}>
                <button className={styles.menuItem} onClick={() => resend('all')}>
                  <span>A todos</span>
                  <span className={styles.menuConteo}>{conCorreo}</span>
                </button>
                <button className={styles.menuItem} onClick={() => resend('unactivated')} disabled={!sinActivar}>
                  <span className={sinActivar ? undefined : styles.atenuado}>Solo a los que no han activado</span>
                  <span className={styles.menuConteo}>{sinActivar}</span>
                </button>
                <button className={styles.menuItem} onClick={() => resend('selected')} disabled={!selected.size}>
                  <span className={selected.size ? undefined : styles.atenuado}>A los seleccionados</span>
                  <span className={styles.menuConteo}>{selected.size}</span>
                </button>
              </div>
            )}
          </div>

          <button className="btn btn-secondary" onClick={download} disabled={!propId || downloading || !data?.rows?.length}>
            {downloading ? <><i className="fas fa-spinner fa-spin" /> Generando...</> : <><i className="fas fa-file-excel" /> Descargar Excel</>}
          </button>
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.aviso}`} onClick={() => setAlert(null)}>
          {alert.message}
        </div>
      )}

      {!propId ? (
        <div className="card"><div className="empty-state">
          <i className={`fas fa-address-book ${styles.vacioIcono}`} />
          <p className={styles.vacioTexto}>Selecciona una propiedad para ver el estado de las cuentas de sus propietarios</p>
        </div></div>
      ) : loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : data && (
        <>
          {/* Resumen */}
          <div className={styles.resumen}>
            {[
              { label: 'Propietarios', value: data.resumen.total, color: '#6c63ff', icon: 'fa-users' },
              { label: 'Cuenta activada', value: data.resumen.activadas, color: '#12b76a', icon: 'fa-user-check' },
              { label: 'Sin activar', value: data.resumen.sin_activar, color: '#f04438', icon: 'fa-user-slash' },
              { label: 'Con teléfono', value: data.resumen.total - data.resumen.sin_telefono, color: '#12b76a', icon: 'fa-phone' },
              ...(data.resumen.rebotaron ? [
                { label: soloRebotados ? 'Correo rebotó · viendo solo estos' : 'Correo rebotó · toca para filtrar',
                  value: data.resumen.rebotaron, color: '#f04438', icon: 'fa-circle-exclamation',
                  onClick: () => setSoloRebotados(v => !v), active: soloRebotados },
              ] : []),
              ...(conAsamblea ? [
                { label: 'Asistieron', value: data.resumen.asistieron, color: '#12b76a', icon: 'fa-clipboard-user' },
                { label: 'Consintieron', value: data.resumen.consintieron, color: '#0ea5e9', icon: 'fa-file-signature' },
              ] : []),
            ].map(k => (
              <div key={k.label} onClick={k.onClick}
                title={k.onClick ? 'Filtrar la tabla por los que rebotaron' : undefined}
                className={`${k.onClick ? styles.kpiClic : styles.kpi} ${k.active ? styles.kpiActivo : ''}`}
                style={{ '--tono': k.color, '--fondo': k.color + '22', '--halo': k.color + '33' }}>
                <div className={styles.kpiIcono}>
                  <i className={`fas ${k.icon} ${styles.kpiGlifo}`} />
                </div>
                <div>
                  <div className={styles.kpiValor}>{k.value}</div>
                  <div className={styles.kpiEtiqueta}>{k.label}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="card">
            <div className={`card-header ${styles.encabezadoTabla}`}>
              <h2>{data.property.name}{conAsamblea ? ` — Asamblea #${data.assembly.number_assembly}` : ''}</h2>
              <div className={styles.filtros}>
                {selected.size > 0 && (
                  <span className={styles.seleccionados}>
                    {selected.size} seleccionado(s)
                    <button onClick={() => setSelected(new Set())} className={styles.limpiar}>
                      limpiar
                    </button>
                  </span>
                )}
                <input className={`form-control ${styles.buscador}`} placeholder="Buscar..." value={q} onChange={e => setQ(e.target.value)} />
                <label className={styles.filtro}>
                  <input type="checkbox" checked={soloProblemas} onChange={e => setSoloProblemas(e.target.checked)} />
                  Solo con datos incompletos
                </label>
                {data.resumen.rebotaron > 0 && (
                  <label className={styles.filtroRebote}>
                    <input type="checkbox" checked={soloRebotados} onChange={e => setSoloRebotados(e.target.checked)} />
                    Solo los que rebotaron
                  </label>
                )}
              </div>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className={styles.colCheck}>
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible}
                        disabled={!seleccionables.length} title="Seleccionar los visibles" />
                    </th>
                    <th>Propietario</th><th>Documento</th><th>Unidad(es)</th><th>Coef.</th>
                    <th>Correo</th><th>Cuenta</th><th>Teléfono</th>
                    {conAsamblea && <><th>Asistió</th><th>Consint.</th></>}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={conAsamblea ? 10 : 8} className={styles.sinResultados}>
                      Sin resultados
                    </td></tr>
                  ) : pageRows.map((r, i) => (
                    <tr key={r.owner_id || i}>
                      <td>
                        <input type="checkbox"
                          checked={selected.has(r.owner_id)}
                          disabled={!r.correo || !r.owner_id}
                          onChange={() => toggleOne(r.owner_id)}
                          title={!r.correo ? 'Sin correo registrado' : ''} />
                      </td>
                      <td className={styles.celdaNombre}>{r.propietario}</td>
                      <td className={styles.celdaDocumento}>{r.documento}</td>
                      <td>{r.unidades}{r.torres ? <span className={styles.torre}> · {r.torres}</span> : null}</td>
                      <td className={styles.celdaChica}>{r.coeficiente}</td>
                      <td className={styles.celdaChica}>
                        {r.correo || <span className={styles.sinCorreo}>—</span>}
                        {r.rebotado && (
                          <span title="Este correo rebotó (Brevo) — no le llegan los mensajes"
                            className={styles.rebote}>
                            <i className={`fas fa-circle-exclamation ${styles.iconoRebote}`} /> rebotó
                          </span>
                        )}
                      </td>
                      <td><Pill ok={r.cuenta_activada} si="Activada" no="Sin activar" /></td>
                      <td>
                        {r.tiene_telefono
                          ? <span className={styles.celdaChica}>{r.telefono}</span>
                          : <Pill ok={false} si="" no="Falta" />}
                      </td>
                      {conAsamblea && <>
                        <td><Pill ok={r.asistio} si="Sí" no="No" /></td>
                        <td><Pill ok={r.consintio} si="Sí" no="No" /></td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
          {rows.length > 0 && <Paginacion pagina={safePage} total={rows.length} porPagina={pageSize} onCambiar={irAPagina}
            opcionesPorPagina={[10, 25, 50, 100]} onCambiarPorPagina={setPageSize} />}
        </>
      )}
    </div>
  );
}
